<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useGetters } from "vuex-composition-helpers";
import { useRoot } from "@/composables/misc/useRoot";
import { useRoute, useRouter } from "vue-router/composables";
import { Nav } from "@humanlyio/interop/nav";

import { MAIN_APP_URL } from "@/common/Env";

import SearchResultsModal from "./SearchResultsModal/Component";

const { currentUser, isAuthenticated, reviewForApprovalCount, isReferralsEnabled } = useGetters([
  "currentUser",
  "isAuthenticated",
  "reviewForApprovalCount",
  "isReferralsEnabled",
]);

const root = useRoot();
const isCollapsed = ref(localStorage.getItem("side-menu-collapsed") === "true");
const route = useRoute();

const operatingSystem = () => {
  switch (true) {
    case /mac/.test(navigator.useragent):
      return "mac";
    case /Win/.test(navigator.useragent):
      return "windows";
    case /Linux/.test(navigator.useragent):
      return "linux";
    default:
      return "mac";
  }
};

const metaKey = (event) => {
  switch (operatingSystem()) {
    case "mac":
      return event.metaKey;
    default:
      return event.ctrlKey;
  }
};

const permissions = () => {
  const userPermissions = currentUser.value;

  const permissionsMap = [
    ["internalUser", userPermissions.actualUserIsInternal],
    ["partnerCSM", userPermissions.actualUserInternalSourcingOnly],
    ["admin", userPermissions.isTeamAdmin],
    ["meetings_self:view", userPermissions.chatbotEnabled],
    ["messaging:view", userPermissions.messagingEnabled],
    ["employeeReferrals", isReferralsEnabled.value],
  ]
  return permissionsMap
    .filter(([, enabled]) => enabled)
    .map(([permission]) => permission);
};

const flags = {
  user: currentUser.value,
  permissions: permissions(),
  teamableBaseUrl: MAIN_APP_URL,
  path: route.path,
  expanded: !isCollapsed.value,
  globalSearch: operatingSystem(),
  clientSideRouting: true,
};

const isLiveSearchVisible = ref(false);
const sudoOptions = ref([]);
const router = useRouter();

const filteredSudoOptions = computed(() => {
  return sudoOptions.value.filter(
    (o) => o.id?.toString() !== currentUser.value.id?.toString()
  );
});

function assignSudoOptions(options) {
  if (options == null) {
    return [];
  }
  return options.map((option) => ({ ...option, redirect: sudoAsLink(option) }));
}

function sudoAsLink(option) {
  return `${MAIN_APP_URL}/admin/users/${option.id
  }/sudo?redirect=${encodeURIComponent(window.location.href)}`;
}

const opsSourcingUrl = computed(() => {
  return `${MAIN_APP_URL}/worker/sourcing_roles?sourcer_id=${currentUser.value.id}`;
});

const adminUserUrl = computed(() => {
  return `${MAIN_APP_URL}/admin/users/stop_sudo?redirect=${MAIN_APP_URL}/admin/users/${currentUser.value.id}/edit`;
});

const adminTeamUrl = computed(() => {
  return `${MAIN_APP_URL}/admin/users/stop_sudo?redirect=${MAIN_APP_URL}/admin/teams/${currentUser.value.primaryTeamId}/edit`;
});
const reviewCountLabel = computed(() => {
  if (
    !isNaN(reviewForApprovalCount.value) &&
    reviewForApprovalCount.value > 0
  ) {
    return { page: "/review/approval", count: reviewForApprovalCount.value };
  }

  return { page: "/review/approval", count: 0 };
});

async function copySudoLinkForPage() {
  const url = `${MAIN_APP_URL}/admin/users/${currentUser.value.id
  }/sudo?redirect=${encodeURIComponent(window.location.href)}`;
  await navigator.clipboard.writeText(url);
  root.$notify({
    position: "bottom-right",
    message: "Sudo link copied",
    title: "Success",
    type: "success",
  });
}

function keydownHandler(event) {
  const isValidCommand = metaKey(event) && event.key.toLowerCase() === "k";

  let isMessageEditor = event.target?.className?.match(
    /ql-editor|el-input__inner|el-select__input|editable|new-value-input|el-textarea__inner/
  );
  isMessageEditor =
    isMessageEditor || event.target.formAction?.includes("templates");
  if (isValidCommand && !isMessageEditor) {
    event.preventDefault();
    openSearch();
  }
}

function sendPage(path) {
  switch (path) {
    case "/teams/admin":
      return router.push({ path: adminTeamUrl });
    case "/users/admin":
      return router.push({ path: adminUserUrl });
    case "/worker":
      return router.push({ path: opsSourcingUrl });
    case "#copy-sudo-link":
      return copySudoLinkForPage();
    default:
      return router.push({ path });
  }
}

function openSearch() {
  isLiveSearchVisible.value = true;
}

const currentPage = computed(() => {
  return route.path;
});

function onCollapseExpandSideMenu(collapsed) {
  localStorage.setItem("side-menu-collapsed", collapsed);
}

onMounted(() => {
  window.addEventListener("keydown", keydownHandler);

  root.$bus.$on("sudo-options-change", assignSudoOptions);
});

onUnmounted(() => {
  window.removeEventListener("keydown", keydownHandler);

  root.$bus.$off("sudo-options-change", assignSudoOptions);
});
</script>

<template>
  <div>
    <Nav
:flags="flags" :nav:page="currentPage" :nav:sudo_users="filteredSudoOptions" :nav:notification="reviewCountLabel"
      @nav:page="sendPage" @nav:open_search="openSearch" @nav:collapsed="onCollapseExpandSideMenu" />
    <SearchResultsModal v-if="isAuthenticated" :visible.sync="isLiveSearchVisible" />
  </div>
</template>

<style scoped>
.elm-wrapper {
  z-index: 1000;
}
</style>
